import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import { IconSearch, IconPlus, IconUsers } from '@tabler/icons-react';

export default function StaffManagement() {
  const { orgId, userProfile } = useAuth();
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const branchId = userProfile?.branchId;
  const resolvedOrgId = orgId || userProfile?.orgId;

  useEffect(() => {
    const load = async () => {
      if (!resolvedOrgId) {
        setLoading(false);
        return;
      }
      try {
        const snap = await getDocs(collection(db, 'organisations', resolvedOrgId, 'users'));
        const allUsers = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const branchStaff = branchId
          ? allUsers.filter(u => u.branchId === branchId && (u.role === 'operator' || u.role === 'driver'))
          : allUsers.filter(u => u.role === 'operator' || u.role === 'driver');
        setStaff(branchStaff);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [resolvedOrgId, branchId]);

  const filtered = staff.filter(s =>
    !search || s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getInitials = name => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const avatarColours = [
    { bg: '#E6F1FB', color: '#185FA5' },
    { bg: '#EAF3DE', color: '#3B6D11' },
    { bg: '#EEEDFE', color: '#534AB7' },
    { bg: '#FCEBEB', color: '#A32D2D' },
    { bg: '#FDF0E8', color: '#993C1D' },
    { bg: '#E1F5EE', color: '#085041' },
  ];

  const roleColour = r => r === 'operator'
    ? { bg: '#EAF3DE', color: '#3B6D11' }
    : { bg: '#EEEDFE', color: '#534AB7' };

  const branchName = branchId
    ? branchId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : 'All branches';

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>Staff</h1>
            <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
              {branchName} · {staff.length} team member{staff.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => navigate('/staff/invite')}
            style={{ background: 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '8px 16px', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <IconPlus size={15} stroke={1.5} /> Invite staff
          </button>
        </div>

        <div style={{ position: 'relative', marginBottom: 16, maxWidth: 320 }}>
          <IconSearch size={15} stroke={1.5} color="var(--color-text-tertiary)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            type="text"
            placeholder="Search staff"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 36, width: '100%', boxSizing: 'border-box', background: '#fff' }}
          />
        </div>

        <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 0.8fr 0.8fr 1fr 80px', gap: 0, padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
            {['Staff member', 'Role', 'Department', 'Assigned asset', ''].map(h => (
              <div key={h} style={{ fontSize: 10, fontWeight: 400, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
            ))}
          </div>

          {loading ? (
            <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: '48px 32px', textAlign: 'center' }}>
              <IconUsers size={32} color="var(--color-text-tertiary)" stroke={1.5} style={{ marginBottom: 12 }} />
              <div style={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 6 }}>
                {search ? 'No staff match your search' : 'No staff yet'}
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                {search ? 'Try a different search term' : 'Click Invite staff to add operators and drivers'}
              </div>
            </div>
          ) : filtered.map((s, i) => {
            const colours = avatarColours[i % avatarColours.length];
            return (
              <div
                key={s.id}
                onClick={() => navigate(`/staff/${s.id}`)}
                style={{ display: 'grid', gridTemplateColumns: '2fr 0.8fr 0.8fr 1fr 80px', gap: 0, padding: '12px 16px', borderBottom: i < filtered.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', cursor: 'pointer', alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: colours.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: colours.color, flexShrink: 0 }}>
                    {getInitials(s.name)}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 400, color: 'var(--color-text-primary)' }}>{s.name || s.email}</div>
                    {!s.onboardingComplete && (
                      <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 1 }}>Invitation pending</div>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: roleColour(s.role).bg, color: roleColour(s.role).color, width: 'fit-content' }}>
                  {s.role ? s.role.charAt(0).toUpperCase() + s.role.slice(1) : '—'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{s.departmentId || '—'}</div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  {s.assignedAssetReg
                    ? <span style={{ fontFamily: 'monospace', fontSize: 11, background: '#FAC775', color: '#412402', padding: '1px 5px', borderRadius: 3 }}>{s.assignedAssetReg}</span>
                    : '—'
                  }
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>View</div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}